import { useMemo, useState } from "react";
import {
  Box,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { StyledTextField } from "../../../components/StyledTextField";
import { StyledButton } from "../../../components/StyledButton";
import { Check } from "@mui/icons-material";
import { NoVisibleIcon, VisibleIcon } from "../../../components/Icons";
import { useFormik } from "formik";
import { validationSchema } from "./schema";
import {
  LoginProvider,
  useLoginContext,
} from "../LoginDefault/context/LoginContext";
import { routesConfig } from "../../../config/routes";
import { useQueryParams } from "../../../hooks/ContextualNavigations";
import { Link } from "react-router-dom";
import { useParamsContext } from "../../../contexts/params/ParamsContext";
import { isHomologationEnvironment } from "../../../config/default";
import { startMicrosoftLogin } from "../../../utils/microsoftLogin";

const Container = (): JSX.Element => {
  const [actionValues, setActionValues] = useState({
    showPassword: false,
  });

  const theme = useTheme();
  const { loginContext } = useLoginContext();
  const { params } = useParamsContext();

  const { query } = useQueryParams();

  const {
    errors,
    values,
    touched,
    isSubmitting,
    handleBlur,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: validationSchema,
    onSubmit: (values) => loginContext.fetchLogin(values),
  });

  const handleClickShowPassword = () => {
    setActionValues((prevState) => ({
      ...prevState,
      showPassword: !prevState.showPassword,
    }));
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
  };

  const verifyEmailToShowCheck = useMemo(
    () => touched.email && !errors.email,
    [touched.email, errors.email]
  );

  return (
    <Stack
      sx={{
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "2rem",
          rowGap: "2rem",
          maxWidth: "50rem",
          width: "95%",
        }}
      >
        <img
          src="https://atem.com.br/wp-content/themes/conatus-atem/img/logo/Logo-Atem-PNG.png"
          alt="Grupo Atem"
          style={{
            width: "13rem",
            height: "7rem",
            objectFit: "fill",
            alignSelf: "center",
          }}
        />

        <Stack>
          <Typography
            sx={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              fontFamily: "Poppins",
              textAlign: "center",
              color: theme.palette.grey[700],
            }}
          >
            Bem-vindo ao Grupo Atem
          </Typography>

          <Typography
            sx={{
              fontFamily: "Poppins",
              textAlign: "center",
              color: theme.palette.grey[700],
            }}
          >
            A sua entrada para os nossos sistemas.
          </Typography>
        </Stack>

        <Divider sx={{ margin: "1rem -2rem" }} />

        <form
          style={{
            display: "flex",
            flexDirection: "column",
            rowGap: "2rem",
          }}
          onSubmit={handleSubmit}
        >
          <Stack sx={{ rowGap: "1rem" }}>
            <StyledTextField
              type="email"
              id="email"
              variant="standard"
              size="small"
              value={values.email}
              onChange={({ target: { value } }) => {
                setFieldValue("email", value);
              }}
              onBlur={handleBlur("email")}
              fullWidth
              placeholder="Digite o email"
              labelprops={{
                label: "E-mail",
                hasTouched: touched.email,
                hasErros: errors.email,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <Check
                        sx={{
                          color: verifyEmailToShowCheck
                            ? "#0BB873"
                            : "transparent",
                        }}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box width={16} />

            <StyledTextField
              type={actionValues.showPassword ? "text" : "password"}
              id="password"
              placeholder="Digite a senha"
              variant="standard"
              size="small"
              value={values.password}
              onChange={(event) => {
                setFieldValue("password", event.target.value);
              }}
              onBlur={handleBlur("password")}
              fullWidth
              labelprops={{
                label: "Senha",
                hasTouched: touched.password,
                hasErros: errors.password,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      sx={{ m: 0 }}
                    >
                      {actionValues.showPassword ? (
                        <NoVisibleIcon />
                      ) : (
                        <VisibleIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          <StyledButton
            type="submit"
            variant="contained"
            fullWidth
            loading={isSubmitting}
            sx={{
              backgroundColor: theme.palette.grey[700],
              "&:hover": {
                backgroundColor: theme.palette.grey[700],
              },
            }}
          >
            Fazer login
          </StyledButton>

          {isHomologationEnvironment() && (
            <StyledButton
              variant="outlined"
              color="primary"
              type="button"
              fullWidth
              onClick={() =>
                startMicrosoftLogin({ url: params.url, rules: params.rules })
              }
            >
              Login Corporativo
            </StyledButton>
          )}
        </form>

        <Divider sx={{ mb: "1.5rem" }} />

        <Link
          to={`${routesConfig.FORGOT_PASSWORD}${query}`}
          style={{ textDecoration: "none", alignSelf: "center" }}
        >
          <Typography
            sx={{ color: theme.palette.grey[700], fontWeight: "bold" }}
          >
            Esqueceu a senha?
          </Typography>
        </Link>
      </Card>
    </Stack>
  );
};

const LoginTrading = (): JSX.Element => {
  return (
    <LoginProvider>
      <Container />
    </LoginProvider>
  );
};

export default LoginTrading;
